<template>
  <PanelPage ref="useAlerts" :loading-overlay="loadingOverlay" md="8">
    <v-card>
      <v-card-title>Modify User</v-card-title>
      <v-card-title>
        <v-autocomplete
          label="User to modify"
          :items="users"
          v-model="userToModifyId"
          item-title="username"
          item-value="id"
        />
      </v-card-title>
      <v-divider/>
      <v-form v-show="userToModifyId" @submit.prevent="submit" @reset="reset">
        <v-card-text class="pb-0">
          <v-text-field
            v-model="form.id.value.value"
            :error-messages="form.id.errorMessage.value"
            label="ID"
            readonly
          />
          <v-text-field
            v-model="form.username.value.value"
            :error-messages="form.username.errorMessage.value"
            label="Username"
          />
          <v-text-field
            v-model="form.email.value.value"
            :error-messages="form.email.errorMessage.value"
            label="Email"
          />
          <v-text-field
            v-model="form.newpassword.value.value"
            :error-messages="form.newpassword.errorMessage.value"
            label="New Password if desired"
            :append-icon="showpwd ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showpwd ? 'text' : 'password'"
            @click:append="showpwd = !showpwd"
          />
          <v-text-field
            v-model="form.renewpassword.value.value"
            :error-messages="form.renewpassword.errorMessage.value"
            label="Re-insert password"
            :append-icon="showpwd ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showpwd ? 'text' : 'password'"
            @click:append="showpwd = !showpwd"
          />
          <v-row no-gutters>
            <v-col>
              <v-checkbox
                v-model="form.is_staff.value.value"
                :error-messages="form.is_staff.errorMessage.value"
                label="RossiniEnergy Staff"
                color="red"
                hide-details
              />
            </v-col>
            <v-col class="d-flex justify-end">
              <v-dialog max-width="500">
                <template v-slot:activator="{ props: activatorProps }">
                  <v-btn
                    v-bind="activatorProps"
                    width="103"
                    :disabled="isExpired"
                    color="red"
                  >{{ isExpired ? 'Expired' : 'Expire' }}
                  </v-btn>
                </template>
                <template v-slot:default="{ isActive }">
                  <v-card title="Dialog">
                    <v-card-text>
                      This action is IRREVERSIBLE, be absolutely sure that you want to expire the user
                      {{ form.username.value.value }}.
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn
                        @click="isActive.value = false"
                      >Cancel
                      </v-btn>
                      <v-btn
                        @click="isActive.value = false;submitExpire()"
                        color="red"
                      >Expire
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </template>
              </v-dialog>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text class="pb-0">
          <v-card-title>ParkAdmin Feature</v-card-title>
          <v-checkbox
            v-model="form.is_parkadmin.value.value"
            :error-messages="form.is_parkadmin.errorMessage.value"
            label="Enable"
          />
          <div v-show="form.is_parkadmin.value.value">
            <v-autocomplete
              label="Managed Parks"
              :items="parks"
              multiple
              chips
              v-model="form.managedparkids.value.value"
              :error-messages="form.managedparkids.errorMessage.value"
              item-title="name"
              item-value="id"
            />
            <v-autocomplete
              label="Child Users"
              :items="users"
              multiple
              chips
              v-model="form.childuserids.value.value"
              :error-messages="form.childuserids.errorMessage.value"
              item-title="username"
              item-value="id"
            />
            <v-card elevation="4">
              <v-card-title>
                <v-checkbox
                  label="ChargeMap Business Integration"
                  v-model="form.cmb_feature.value.value"
                  hide-details
                />
              </v-card-title>
              <v-card-text>
                <v-text-field
                  v-model="form.cmb_org_id.value.value"
                  :error-messages="form.cmb_org_id.errorMessage.value"
                  type="number"
                  label="Organization ID"
                  :readonly="!form.cmb_feature.value.value"
                />
                <v-text-field
                  v-model="form.cmb_token_id.value.value"
                  :error-messages="form.cmb_token_id.errorMessage.value"
                  label="Token ID"
                  :readonly="!form.cmb_feature.value.value"
                />
              </v-card-text>
            </v-card>
          </div>
          <ManualEnabledCS
            v-model="form.authorized_cs.value.value"
            :parks="parks"
          />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn type="reset">Reset</v-btn>
          <v-btn type="submit" variant="elevated" color="green">Submit</v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </PanelPage>
</template>

<script setup>
import { onMounted, ref, watch } from "vue";
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { getFormFromSchema } from "../utils.js";
import PanelPage from "../components/PanelPage.vue";
import ManualEnabledCS from "../components/ManualEnabledCS.vue";
import { fetchUser, fetchUsers, modifyPatchUser, expireUser } from "../remote/user.js";
import { fetchParks } from "../remote/park.js";

// State
const showpwd = ref(false);
const useAlerts = ref(null);
const users = ref([]);
const parks = ref([]);
const userToModifyId = ref(null); // From the main selector
const originalData = ref(null);
const loadingOverlay = ref(false);
const isExpired = ref(false);

watch(userToModifyId, async (newValue) => {
  if (newValue !== null) {
    loadingOverlay.value = true;
    await setUser(newValue);
    loadingOverlay.value = false;
  }
});

// Form
// noinspection JSCheckFunctionSignatures
const schema = yup.object({
  id: yup.number(),
  username: yup.string().required(),
  email: yup.string().email(),
  newpassword: yup.string(),
  renewpassword: yup.string().oneOf([yup.ref('newpassword'), null], "Passwords do not match"),
  is_staff: yup.bool(),
  is_parkadmin: yup.bool(),
  childuserids: yup.array(),
  managedparkids: yup.array(),
  authorized_cs: yup.array(),
  cmb_feature: yup.boolean(),
  cmb_org_id: yup.number().when(
    "cmb_feature", {
      is: true,
      then: (schema) => schema.required().min(1),
      otherwise: (schema) => schema.notRequired(),
    }),
  cmb_token_id: yup.string().when(
    "cmb_feature", {
      is: true,
      then: (schema) => schema.required().min(36),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const { handleSubmit, handleReset } = useForm({
  validationSchema: schema
});

const form = getFormFromSchema(schema);

async function setUser(user_id) {
  try {
    let user;
    try {
      user = await fetchUser(user_id);
      console.log(user);
    } catch (e) {
      useAlerts.value.errorAlert(`No User with ID ${user_id} in the system`, e.toString());
      return;
    }
    // Fields = Values
    originalData.value = user;
    isExpired.value = user['is_readonly'];
    form.id.value.value = user['id'];
    form.username.value.value = user['username'];
    form.email.value.value = user['email'];
    form.is_staff.value.value = user['is_staff'];
    form.is_parkadmin.value.value = user['feature_flags']['parkadmin'];
    if (form.is_parkadmin.value.value === true) {
      const pa = user['features']['parkadmin'];
      form.childuserids.value.value = pa['child_users'];
      form.managedparkids.value.value = pa['managed_parks'];
      form.cmb_feature.value.value = pa['cmb_feature'];
      form.cmb_org_id.value.value = pa['cmb_org_id'];
      form.cmb_token_id.value.value = pa['cmb_token_id'];
    }
    form.authorized_cs.value.value = user['features']['charge']['authorized_cs'];
  } catch (error) {
    useAlerts.value.errorAlert('Impossible to load the user', error.toString());
  }
}

// noinspection JSValidateTypes
const submit = handleSubmit(async (values) => {
  let payload = {
    username: values.username,
    email: values.email,
    is_staff: values.is_staff,
    features: {
      charge: {
        authorized_cs: values.authorized_cs,
      }
    }
  };
  if (values.newpassword !== undefined && values.newpassword !== '') {
    if (values.newpassword !== values.renewpassword) {
      useAlerts.value.errorAlert('Password Mismatch!', '');
      return;
    }
    payload.password = values.newpassword;
  }
  if (values.is_parkadmin === false && originalData.value['feature_flags']['parkadmin'] === true) {
    payload.features.parkadmin = null;
  } else if (values.is_parkadmin === true) {
    payload.features.parkadmin = {
      managed_parks: values.managedparkids,
      child_users: values.childuserids,
    };
    payload.features.parkadmin.cmb_feature = values.cmb_feature ?? false;
    if (values.cmb_feature ?? false) {
      payload.features.parkadmin.cmb_org_id = values.cmb_org_id;
      payload.features.parkadmin.cmb_token_id = values.cmb_token_id;
    }
  }
  loadingOverlay.value = true;
  // Modify User via PATCH
  try {
    await modifyPatchUser(useAlerts, payload, values.id);
  } catch (e) {
    useAlerts.value.errorAlert('Error during User save', e.toString());
  } finally {
    loadingOverlay.value = false;
  }
});

async function reset() {
  // noinspection JSValidateTypes
  handleReset();
  await setUser(userToModifyId.value);
}

async function submitExpire() {
  loadingOverlay.value = true;
  try {
    await expireUser(useAlerts, userToModifyId.value);
    isExpired.value = true;
  } catch (e) {
    // nop
  } finally {
    loadingOverlay.value = false;
  }
}

// Hook
onMounted(async () => {
  loadingOverlay.value = true;
  try {
    [parks.value, users.value] = await Promise.all([fetchParks(), fetchUsers()]);
  } catch (e) {
    useAlerts.value.errorAlert('Impossible to load initial infos', e.toString());
  } finally {
    loadingOverlay.value = false;
  }
});
</script>

<style scoped>

</style>
